import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import {
  Package,
  ShoppingCart,
  Users,
  IndianRupee,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, analyticsRes] = await Promise.all([
        adminApi.getDashboardStats(),
        adminApi.getAnalytics(period),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = () => {
    if (!analytics) return [];
    const ordersByDate = analytics.orders_by_date || {};
    const revenueByDate = analytics.revenue_by_date || {};
    
    return Object.keys(ordersByDate).sort().map((date) => ({
      date: date.substring(5),
      orders: ordersByDate[date] || 0,
      revenue: revenueByDate[date] || 0,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const chartData = prepareChartData();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-primary font-semibold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here's your store overview.</p>
        </div>
        <div className="flex gap-2">
          {['7d', '30d', '90d'].map((p) => (
            <Button
              key={p}
              variant={period === p ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p)}
              className={period === p ? 'bg-primary' : ''}
              data-testid={`period-${p}`}
            >
              {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-base card-hover" data-testid="stat-revenue">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Revenue</p>
                <p className="text-2xl font-heading font-bold text-primary mt-1">
                  {formatCurrency(stats?.revenue?.total || 0)}
                </p>
              </div>
              <div className="p-3 bg-accent/10 rounded-full">
                <IndianRupee className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base card-hover" data-testid="stat-orders">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Orders</p>
                <p className="text-2xl font-heading font-bold text-primary mt-1">
                  {stats?.orders?.total || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.orders?.pending || 0} pending
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <ShoppingCart className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base card-hover" data-testid="stat-products">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Products</p>
                <p className="text-2xl font-heading font-bold text-primary mt-1">
                  {stats?.products?.total || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.products?.active || 0} active
                </p>
              </div>
              <div className="p-3 bg-secondary/50 rounded-full">
                <Package className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base card-hover" data-testid="stat-customers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Customers</p>
                <p className="text-2xl font-heading font-bold text-primary mt-1">
                  {stats?.customers?.total || 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-primary flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0B522B" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0B522B" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="date" stroke="#57534e" fontSize={12} />
                  <YAxis stroke="#57534e" fontSize={12} tickFormatter={(v) => `₹${v/1000}k`} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: '8px' }}
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#0B522B"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Orders Chart */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-primary flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Orders Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="date" stroke="#57534e" fontSize={12} />
                  <YAxis stroke="#57534e" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: '8px' }}
                    formatter={(value) => [value, 'Orders']}
                  />
                  <Line
                    type="monotone"
                    dataKey="orders"
                    stroke="#D4AF37"
                    strokeWidth={2}
                    dot={{ fill: '#D4AF37', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Low Stock Alert */}
        <Card className="card-base">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-primary flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Low Stock Alert
            </CardTitle>
            <Link to="/inventory">
              <Button variant="ghost" size="sm" className="text-primary">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.low_stock?.length > 0 ? (
                stats.low_stock.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center justify-between py-2 border-b border-[#E7E5E4] last:border-0">
                    <span className="text-sm font-medium text-primary truncate max-w-[180px]">
                      {item.name}
                    </span>
                    <Badge variant="outline" className="badge-error">
                      {item.stock} left
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  All products are well stocked!
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="card-base lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-primary">Recent Orders</CardTitle>
            <Link to="/orders">
              <Button variant="ghost" size="sm" className="text-primary">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_orders?.length > 0 ? (
                stats.recent_orders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between py-2 border-b border-[#E7E5E4] last:border-0">
                    <div className="flex-1">
                      <p className="text-sm font-mono text-primary font-medium">{order.order_number}</p>
                      <p className="text-xs text-muted-foreground">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-primary">{formatCurrency(order.total)}</p>
                      <Badge className={`${getStatusColor(order.status)} text-xs`}>
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent orders
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cart & Wishlist Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-primary">Active Carts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-heading font-bold text-primary">
                  {analytics?.carts?.active || 0}
                </p>
                <p className="text-sm text-muted-foreground">carts with items</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-semibold text-accent">
                  {formatCurrency(analytics?.carts?.total_value || 0)}
                </p>
                <p className="text-sm text-muted-foreground">potential revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-primary">Wishlists</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-heading font-bold text-primary">
                  {analytics?.wishlists?.active || 0}
                </p>
                <p className="text-sm text-muted-foreground">active wishlists</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-primary">Top Wishlisted:</p>
                {analytics?.wishlists?.top_products?.slice(0, 2).map((p, i) => (
                  <p key={i} className="text-xs text-muted-foreground truncate max-w-[150px]">
                    {p.name} ({p.count})
                  </p>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
